/* ── BycAccordion ──────────────────────────────────────────────────── */
class BycAccordion extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      trigger: '[data-accordion-trigger]',
      content: '[data-accordion-content]',
      item: '[data-accordion-item]',
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener('click', (e) => {
      const trigger = e.target.closest(this.selectors.trigger);
      if (!trigger) return;

      const item = trigger.closest(this.selectors.item);
      if (!item) return;

      const content = item.querySelector(':scope > [data-accordion-content]');
      if (!content) return;

      const isOpen = item.classList.contains('is-open');

      if (isOpen) {
        this.close(content, item);
      } else {
        this.open(content, item);
      }
    });
  }

  // Cancel any pending transitionend callback stored on the content element.
  _cancelPending(content) {
    if (content._bycAbort) {
      content._bycAbort();
      content._bycAbort = null;
    }
  }

  // Attach a self-cancelling transitionend listener. Returns a cancel fn.
  _onTransitionEnd(content, callback) {
    let cancelled = false;
    const handler = () => {
      if (cancelled) return;
      content._bycAbort = null;
      callback();
    };
    content.addEventListener('transitionend', handler, { once: true });
    content._bycAbort = () => {
      cancelled = true;
      content.removeEventListener('transitionend', handler);
    };
  }

  open(content, item) {
    // Close sibling [data-accordion-item] elements within the same parent container.
    [...item.parentElement.children].forEach((sib) => {
      if (sib === item || !sib.hasAttribute('data-accordion-item')) return;
      if (!sib.classList.contains('is-open')) return;
      const sibContent = sib.querySelector(':scope > [data-accordion-content]');
      if (sibContent) this.close(sibContent, sib);
    });

    // When the root item of this byc-accordion opens, close all sibling byc-accordion elements.
    if (item.parentElement === this) {
      [...(this.parentElement?.children ?? [])].forEach((sib) => {
        if (sib === this || sib.tagName !== 'BYC-ACCORDION') return;
        const sibItem = sib.querySelector(':scope > [data-accordion-item]');
        if (sibItem?.classList.contains('is-open')) {
          const sibContent = sibItem.querySelector(':scope > [data-accordion-content]');
          if (sibContent) sib.close(sibContent, sibItem);
        }
      });
    }

    item.classList.add('is-open');

    // Cancel any stale close/open listener before starting.
    this._cancelPending(content);

    content.style.display = 'block';

    // Cancel any in-flight transition so scrollHeight is measured from a stable state.
    content.style.transition = 'none';
    const height = content.scrollHeight;
    content.style.height = '0px';
    content.offsetHeight; // reflow

    content.style.transition = 'height 0.35s ease';
    content.style.height = height + 'px';

    this._onTransitionEnd(content, () => {
      // Guard: only apply if still open.
      if (item.classList.contains('is-open')) {
        content.style.height = 'auto';
        content.style.transition = '';
      }
    });
  }

  close(content, item) {
    item.classList.remove('is-open');

    // Cancel any stale open/close listener before starting.
    this._cancelPending(content);

    // Cancel any in-flight transition first, then snapshot the current rendered
    // height before animating to 0. Without this, a rapid open→close would read
    // scrollHeight (full intrinsic height) and snap the element to full height
    // before collapsing — causing the visible "open-close flash" glitch.
    content.style.transition = 'none';
    content.style.height = content.getBoundingClientRect().height + 'px';
    content.offsetHeight; // reflow

    content.style.transition = 'height 0.35s ease';
    content.style.height = '0px';

    this._onTransitionEnd(content, () => {
      // Guard: only hide if still closed.
      if (!item.classList.contains('is-open')) {
        content.style.display = 'none';
        content.style.height = '';
        content.style.transition = '';
      }
    });
  }
}

customElements.define('byc-accordion', BycAccordion);


/* ── BycCapsule ────────────────────────────────────────────────────── */
class BycCapsule extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    const canvas = this.querySelector('canvas.byc-canvas');
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 1000);
    // Start far back for intro animation
    this.camera.position.set(0.2565371238916951, 0.5461686903232021, 22);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
    this.renderer.autoClear = false;
    this.renderer.setPixelRatio(window.devicePixelRatio);
    // false = don't override CSS width/height styles
    this.renderer.setSize(width, height, false);
    this.renderer.setClearColor(0xF6F5F1);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 4;
    this.renderer.domElement.style.opacity = '0';
    this.renderer.domElement.style.transition = 'opacity 0.8s ease';

    this.orbit = new THREE.OrbitControls(this.camera, this.renderer.domElement);
    this.orbit.minDistance = 6;
    this.orbit.maxDistance = 16;
    this.orbit.enabled = false;
    this.orbit.addEventListener('change', () => this.render());

    window.addEventListener('resize', () => this.onWindowResize());

    this.loadModel();
  }

  onWindowResize() {
    const canvas = this.renderer.domElement;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.render();
  }

  render() {
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
  }

  loadModel() {
    const rgbeLoader = new THREE.RGBELoader();
    const gltfLoader = new THREE.GLTFLoader();

    rgbeLoader.load(this.dataset.hdr, (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      this.scene.environment = texture;

      gltfLoader.load(this.dataset.model, (gltf) => {
        const model = gltf.scene;
        this.scene.add(model);

        // Store references for runtime manipulation.
        this._model = model;
        this._textureLoader = new THREE.TextureLoader();

        // Backdrop meshes are decals on the capsule surface.
        // This GLTF's decal normals are inverted, so BackSide is the visible face.
        // Keep single-sided rendering + depth test to avoid mirrored bleed-through.
        model.getObjectByName('Backdrop').renderOrder    = 2;
        model.getObjectByName('Backdrop_1').renderOrder  = 2;
        model.getObjectByName('Backdrop').material.visible     = false;
        model.getObjectByName('Backdrop_1').material.visible   = false;
        model.getObjectByName('Backdrop').material.toneMapped  = false;
        model.getObjectByName('Backdrop_1').material.toneMapped = false;
        model.getObjectByName('Backdrop').material.side = THREE.BackSide;
        model.getObjectByName('Backdrop_1').material.side = THREE.BackSide;
        model.getObjectByName('Backdrop').material.depthTest = true;
        model.getObjectByName('Backdrop_1').material.depthTest = true;
        model.getObjectByName('Backdrop').material.depthWrite = false;
        model.getObjectByName('Backdrop_1').material.depthWrite = false;
        model.getObjectByName('Backdrop').material.polygonOffset = true;
        model.getObjectByName('Backdrop_1').material.polygonOffset = true;
        model.getObjectByName('Backdrop').material.polygonOffsetFactor = -1;
        model.getObjectByName('Backdrop_1').material.polygonOffsetFactor = -1;
        model.getObjectByName('Backdrop').material.polygonOffsetUnits = -1;
        model.getObjectByName('Backdrop_1').material.polygonOffsetUnits = -1;
        model.getObjectByName('Roundcube001_Material001_0').material.color.set('#cbc9c2');
        model.getObjectByName('Roundcube_Material_0').material.color.set('#cbc9c2');

        // Snapshot the GLTF material state so we can restore it on type-switch / erase.
        this._origMat = {};
        ['Roundcube001_Material001_0', 'Roundcube_Material_0'].forEach(name => {
          const m = model.getObjectByName(name)?.material;
          if (!m) return;
          this._origMat[name] = {
            color:       m.color.getHex(),
            transparent: m.transparent,
            opacity:     m.opacity,
            metalness:   m.metalness,
            roughness:   m.roughness,
            depthWrite:  m.depthWrite,
            toneMapped:  m.toneMapped,
            envMapIntensity: m.envMapIntensity ?? 1,
          };
        });

        this.playIntroAnimation(model);
      });
    });
  }

  // ── 3D model manipulation API ──────────────────────────────────────
  // Mapping of logical part ids to 3D object names.
  _partNames(part) {
    const map = {
      cap:  { color: 'Roundcube001_Material001_0', print: 'Backdrop'   },
      body: { color: 'Roundcube_Material_0',        print: 'Backdrop_1' },
    };
    return map[part] ?? null;
  }

  // Material profile per capsule type — mirrors the reference's data-* attributes.
  // Called by setPartColor when a colour is actually applied (not on reset).
  _getMaterialProfile() {
    switch (this._capsuleType) {
      case 'LIQ_CAPS':
        // Semi-transparent, emissive "liquid inside" look.
        return { metalness: 0, roughness: 0, opacity: 0.3, transparent: true,
                 emissiveIntensity: 1, depthWrite: false, toneMapped: true };
      default:
        // GEL_CAPS / K_CAPS / AR_CAPS / FLV_CAPS — glossy solid plastic.
        // depthWrite:true makes the capsule render solidly (no back-face bleed).
        // Backdrop uses renderOrder+depthTest:false to always draw on top instead.
        // toneMapped:false bypasses ACESFilmic so the colour value is exact.
        return { metalness: 0, roughness: 0, opacity: 1, transparent: false,
                 emissiveIntensity: 0, depthWrite: true, toneMapped: false };
    }
  }

  setPartColor(part, hex) {
    if (!this._model) return;
    const names = this._partNames(part);
    if (!names) return;
    const obj = this._model.getObjectByName(names.color);
    if (!obj) return;
    const p = this._getMaterialProfile();
    obj.material.color.set(hex);
    obj.material.metalness       = p.metalness;
    obj.material.roughness       = p.roughness;
    obj.material.opacity         = p.opacity;
    obj.material.transparent     = p.transparent;
    obj.material.emissiveIntensity = p.emissiveIntensity;
    obj.material.depthWrite      = p.depthWrite;
    obj.material.toneMapped      = p.toneMapped;
    obj.material.needsUpdate     = true;
    this.render();
  }

  setBandaColor(hex) {
    if (!this._model) return;
    const obj = this._model.getObjectByName('banda');
    if (!obj) return;
    obj.material.color.set(hex);
    obj.material.needsUpdate = true;
    this.render();
  }

  applyPrint(part, dataUrl, rotation = 0) {
    if (!this._model) return;
    const names = this._partNames(part);
    if (!names) return;
    const obj = this._model.getObjectByName(names.print);
    if (!obj) return;

    // Dispose old texture to avoid memory leak.
    if (obj.material.map) { obj.material.map.dispose(); obj.material.map = null; }

    // White base color so it doesn't bleed black behind transparent/semi-transparent images.
    obj.material.color.set('#ffffff');
    // Allow alpha channel (PNG logos with transparent backgrounds).
    obj.material.transparent = true;
    obj.material.alphaTest   = 0.01;
    // Depth test + single-sided decals prevent mirrored text on opposite side.
    obj.material.depthTest   = true;
    obj.material.depthWrite  = false;
    obj.material.side        = THREE.BackSide;

    // Track in-flight texture loads so captureImage() can wait for them.
    this._pendingTextures = (this._pendingTextures ?? 0) + 1;

    obj.material.map = this._textureLoader.load(dataUrl, (tex) => {
      // flipY = true is the Three.js default and matches the Backdrop UV orientation.
      tex.encoding  = THREE.sRGBEncoding;
      tex.center.set(0.5, 0.5);
      tex.rotation  = 2 * Math.PI * (rotation / 360);
      tex.needsUpdate      = true;
      obj.material.visible    = true;
      obj.material.toneMapped = false;
      obj.material.needsUpdate = true;
      this._pendingTextures = Math.max(0, (this._pendingTextures ?? 1) - 1);
      this.render();
    });
  }

  /**
   * Returns a Promise that resolves once all in-flight texture loads finish.
   * Polls every 50 ms; gives up after 10 s to avoid hanging forever.
   */
  waitForTextures() {
    return new Promise((resolve) => {
      if (!this._pendingTextures) { resolve(); return; }
      const start    = Date.now();
      const interval = setInterval(() => {
        if (!this._pendingTextures || Date.now() - start > 10000) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  }

  clearPrint(part) {
    if (!this._model) return;
    const names = this._partNames(part);
    if (!names) return;
    const obj = this._model.getObjectByName(names.print);
    if (!obj) return;
    if (obj.material.map) { obj.material.map.dispose(); obj.material.map = null; }
    obj.material.visible     = false;
    obj.material.transparent = false;
    obj.material.alphaTest   = 0;
    obj.material.depthTest   = true;
    obj.material.depthWrite  = false;
    obj.material.side        = THREE.BackSide;
    obj.material.color.set('#ffffff');
    obj.material.needsUpdate = true;
    this.render();
  }

  resetPosition() {
    if (this._model) this._model.rotation.set(0, 0, 0);
    this.camera.position.set(0.2565371238916951, 0.5461686903232021, 10.374771730779235);
    this.orbit.reset();
    this.render();
  }

  /**
   * Render the current scene to an off-screen canvas at the given resolution
   * and return a PNG data URL. This produces a clean, non-cropped image of
   * exactly the configured dimensions regardless of the on-screen canvas size.
   */
  captureImage(width = 1200, height = 900) {
    // We MUST reuse the existing renderer and its WebGL context.
    // Creating a second renderer gives it a fresh context with no textures,
    // so logos/text applied via applyPrint() would be missing from the capture.

    const canvas = this.renderer.domElement;

    // --- snapshot current state ---
    const origWidth  = canvas.clientWidth  || canvas.width;
    const origHeight = canvas.clientHeight || canvas.height;
    const origAspect = this.camera.aspect;
    const origPixelRatio = this.renderer.getPixelRatio();

    // Resize renderer to capture resolution (setSize with false keeps CSS untouched).
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    // Render the scene at the new resolution.
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);

    const dataUrl = canvas.toDataURL('image/png');

    // --- restore original state ---
    this.renderer.setPixelRatio(origPixelRatio);
    this.renderer.setSize(origWidth, origHeight, false);
    this.camera.aspect = origAspect;
    this.camera.updateProjectionMatrix();
    this.render(); // redraw on screen at original size

    return dataUrl;
  }

  eraseAll() {
    if (!this._model) return;
    // Same reset as applyTypeReset — restore the glass-like default.
    ['Roundcube001_Material001_0', 'Roundcube_Material_0'].forEach(name => {
      const obj = this._model.getObjectByName(name);
      if (!obj) return;
      obj.material.color.set('#cbc9c2');
      obj.material.transparent     = true;
      obj.material.opacity         = 0.3;
      obj.material.depthWrite      = false;
      obj.material.toneMapped      = true;
      obj.material.metalness       = 0;
      obj.material.roughness       = 0;
      obj.material.emissiveIntensity = 1;
      obj.material.needsUpdate     = true;
    });
    ['cap', 'body'].forEach(p => this.clearPrint(p));

    // Reset banda color to default white (matches reference erase behaviour).
    const banda = this._model.getObjectByName('banda');
    if (banda) {
      banda.material.color.set('#ffffff');
      banda.material.needsUpdate = true;
    }

    this.render();
  }

  _restoreOriginalMaterials() {
    if (!this._origMat) return;
    Object.entries(this._origMat).forEach(([name, orig]) => {
      const m = this._model.getObjectByName(name)?.material;
      if (!m) return;
      m.color.setHex(orig.color);
      m.transparent       = orig.transparent;
      m.opacity           = orig.opacity;
      m.metalness         = orig.metalness;
      m.roughness         = orig.roughness;
      m.depthWrite        = orig.depthWrite;
      m.toneMapped        = orig.toneMapped;
      m.envMapIntensity   = orig.envMapIntensity;
      m.needsUpdate       = true;
    });
  }

  // hasBand=true for LIQ_CAPS (banda is the thick band/thread in the center).
  applyTypeReset(hasBand = false, type = '') {
    if (!this._model) return;

    // Store type so setPartColor can look up the correct material profile.
    this._capsuleType = type;

    // Reset to reference setTipo() values — translucent glassy capsule.
    // This is the "no colour picked yet" state, same for all types.
    ['Roundcube001_Material001_0', 'Roundcube_Material_0'].forEach(name => {
      const obj = this._model.getObjectByName(name);
      if (!obj) return;
      obj.material.color.set('#cbc9c2');
      obj.material.transparent     = true;
      obj.material.opacity         = 0.3;
      obj.material.depthWrite      = false;
      obj.material.toneMapped      = true;
      obj.material.metalness       = 0;
      obj.material.roughness       = 0;
      obj.material.emissiveIntensity = 1;
      obj.material.needsUpdate     = true;
    });

    // Banda (center band) — show solid for LIQ_CAPS, hide for everything else.
    const banda = this._model.getObjectByName('banda');
    if (banda) {
      if (hasBand) {
        banda.material.transparent = false;
        banda.material.depthWrite  = true;
        banda.material.toneMapped  = false;
      } else {
        banda.material.transparent = true;
        banda.material.depthWrite  = false;
        banda.material.toneMapped  = true;
      }
      banda.material.needsUpdate = true;
    }

    ['cap', 'body'].forEach(p => this.clearPrint(p));
    this.render();
  }

  playIntroAnimation(model) {
    const targetZ = 10.374771730779235;
    const startZ = 22;
    const duration = 1800;
    const startTime = performance.now();

    // Fade in the canvas
    requestAnimationFrame(() => {
      this.renderer.domElement.style.opacity = '1';
    });

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease out cubic + slight overshoot (elastic feel)
      const eased = 1 - Math.pow(1 - t, 4);

      this.camera.position.z = startZ + (targetZ - startZ) * eased;

      // Slow Y rotation that settles to 0
      model.rotation.y = (1 - eased) * Math.PI * 1.5;

      this.render();

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        this.camera.position.z = targetZ;
        model.rotation.y = 0;
        this.orbit.enabled = true;
        this.render();
      }
    };

    requestAnimationFrame(animate);
  }
}
customElements.define('byc-capsule', BycCapsule);


/* ── BycColorPicker ────────────────────────────────────────────────── */
class BycColorPicker extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._picker = null;
    this._observer = null;
  }

  connectedCallback() {
    // Eagerly init the picker so its HTML is injected at page load, before any
    // accordion animation runs. This ensures scrollHeight measurements in
    // BycAccordion.open() are accurate — preventing the height-jump glitch where
    // the animation targets a stale height and then the picker HTML appears late.
    //
    // The 0x0 canvas guard in color-picker-wc.js prevents draw errors while
    // the picker sits inside a collapsed accordion. The IntersectionObserver
    // below then calls update() to draw/redraw the canvas whenever the picker
    // actually becomes visible (first open + tab switch back).
    if (!this._initialized) {
      this._initialized = true;
      this._initPicker();
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (this._picker) {
          this._picker.update();
        }
      },
      { threshold: 0.01 }
    );
    this._observer.observe(this);
  }

  _initPicker() {
    this._picker = new ColorPickerControl({
      container: this,
      theme: 'default',
      useAlpha: false,
      color: { r: 255, g: 255, b: 255 },
    });

    // Match brightness-control height to the wheel control's rendered height.
    const wheelControl = this.querySelector('.color-picker-wheel-control');
    const group = this.querySelector('.color-picker-controls-group');
    if (wheelControl && group) {
      group.style.setProperty('--height', wheelControl.scrollHeight + 'px');
    }

    this._picker.on('change', (color) => {
      const hex = color.toHEX();
      console.log('[BycColorPicker] color changed:', { h: color.h, s: color.s, v: color.v, hex });
      // Bubble a custom event so parent components can react to color changes.
      this.dispatchEvent(new CustomEvent('byc:color-change', {
        bubbles: true,
        detail: { h: color.h, s: color.s, v: color.v, hex },
      }));
    });
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._picker) {
      this._picker.dispose();
      this._picker = null;
    }
  }
}

customElements.define('byc-color-picker', BycColorPicker);


/* ── BycControls ─────────────────────────────────────────────────── */
class BycControls extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      typeItem: '.byc-opt__type-item',
      sizeItem: '.byc-opt__size-item',
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener('click', (e) => {
      const typeItem = e.target.closest(this.selectors.typeItem);
      if (typeItem) {
        this.selectType(typeItem);
        return;
      }
      const sizeItem = e.target.closest(this.selectors.sizeItem);
      if (sizeItem) this.selectSize(sizeItem);
    });

    // ── Capsule colour swatch radios → apply to 3D model ──────────────
    this.addEventListener('change', (e) => {
      const radio = e.target;
      if (radio.type !== 'radio' || !radio.name.startsWith('capsule-color-')) return;

      // name formats:
      //   capsule-color-{type}-{finish}-{part}   (natural / metallic)
      //   capsule-color-{type}-flavour            (flavour, no part)
      //   capsule-color-{type}-traditional-{part} (traditional wheel, emits byc:color-change)
      const segments = radio.name.split('-');
      const part   = segments[segments.length - 1]; // cap | body | flavour
      const finish = segments[segments.length - 2]; // natural | metallic | traditional | color (flavour)
      const hex    = radio.dataset.hex ?? radio.value; // natural/metallic store hex in data-hex

      this._state.color = this._state.color || {};

      if (part === 'flavour') {
        // Flavour applies same colour to both cap and body — store into both keys.
        const data = { finish, hex, name: radio.dataset.name ?? radio.value };
        this._state.color.cap    = data;
        this._state.color.body   = data;
        // Clear any stale individual cap/body selections from a previous type.
        delete this._state.color.flavour;
      } else {
        this._state.color[part] = { finish, hex, name: radio.dataset.name ?? radio.value };
      }

      console.log('[BycControls] colour selected:', JSON.stringify(this._state, null, 2));

      const capsule = document.querySelector('byc-capsule');
      if (part === 'flavour') {
        // Flavour swatches apply the same colour to both cap and body.
        if (capsule) {
          capsule.setPartColor('cap',  hex);
          capsule.setPartColor('body', hex);
        }
        // Re-assert prints so they aren't lost after the color change.
        this._renderPart('cap');
        this._renderPart('body');
      } else {
        if (capsule) capsule.setPartColor(part, hex);
        this._renderPart(part);
      }
      this._notifySummary();
    });

    // ── Traditional colour picker custom event ─────────────────────────
    this.addEventListener('byc:color-change', (e) => {
      const pickerEl = e.target.closest('[tab-content]');
      const part     = pickerEl?.dataset.id ?? null;
      if (!part) return;

      this._state.color = this._state.color || {};
      this._state.color[part] = { finish: 'traditional', hex: e.detail.hex };

      console.log('[BycControls] traditional colour:', JSON.stringify(this._state, null, 2));

      const capsule = document.querySelector('byc-capsule');
      if (part === 'branding') {
        if (capsule) capsule.setBandaColor(e.detail.hex);
      } else {
        if (capsule) capsule.setPartColor(part, e.detail.hex);
        // Re-assert print so it isn't lost after the color change.
        this._renderPart(part);
      }
      this._notifySummary();
    });

    // Auto-select the first type on load (matches Liquid-rendered is-active).
    const first = this.querySelector(this.selectors.typeItem);
    if (first) this.selectType(first);

    this.initPrinting();

    // ── Reset position button ──────────────────────────────────────────
    this.querySelector('[position-reset]')?.addEventListener('click', () => {
      document.querySelector('byc-capsule')?.resetPosition();
    });

    // ── Erase all button ──────────────────────────────────────────────
    this.querySelector('[erase-settings]')?.addEventListener('click', () => this._eraseAll());
  }

  _eraseAll() {
    document.querySelector('byc-capsule')?.eraseAll();
    this._state = { cap: { logo: {}, text: {} }, body: { logo: {}, text: {} }, color: {} };
    this.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);
    this.querySelectorAll('.byc-text-input').forEach(i => i.value = '');
    this.querySelectorAll('.byc-upload-preview').forEach(img => { img.src = ''; img.setAttribute('hidden', ''); });
    this.querySelectorAll('.byc-upload-placeholder').forEach(p => p.removeAttribute('hidden'));
    this.querySelectorAll('.byc-upload-input').forEach(i => i.value = '');
    console.log('[BycControls] erased all');
    this._notifySummary();
  }

  // ── Printing state + 3D wiring ─────────────────────────────────────────
  _createTextDataUrl(text, color, sizeName) {
    const fontSizeMap = { large: 100, medium: 80, small: 60 };
    let px = fontSizeMap[sizeName] ?? 58;  // must be let — shrink loop decrements it
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const maxWidth = canvas.width - 32;

    // Shrink font until text fits, matching reference createText() logic.
    do {
      ctx.font = `bold ${px}px Arial`;
      if (ctx.measureText(text).width <= maxWidth || px <= 8) break;
      px--;
    } while (true);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color ?? '#ffffff';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL();
  }

  // Notify the summary to live-update and re-validate the quote button.
  _notifySummary() {
    document.dispatchEvent(new CustomEvent('byc:state-change'));
    document.dispatchEvent(new CustomEvent('byc:validation-change', { detail: this._getValidationErrors() }));
  }

  /**
   * Return an array of human-readable error strings for any required field
   * that hasn't been filled yet. Empty array = valid.
   */
  _getValidationErrors() {
    const errors = [];

    // Size is always required.
    const sizeChecked = !!this.querySelector('input[name="capsule-size"]:checked');
    if (!sizeChecked) errors.push('Please select a capsule size.');

    // Color is only required when the selected type actually has colour options.
    if (this._typeHasColors) {
      const colors = this._state?.color ?? {};
      const hasColor = !!(colors.cap?.hex || colors.body?.hex);
      if (!hasColor) errors.push('Please select a capsule color.');
    }

    return errors;
  }

  // Re-apply whatever print is active for a part — used after color/type changes.
  _renderPart(part) {
    const s = this._state?.[part];
    const c = document.querySelector('byc-capsule');
    if (!s || !c) return;
    if (s.logo?.dataUrl) {
      c.applyPrint(part, s.logo.dataUrl, s.logo.rotation ?? 0);
    } else if (s.text?.content) {
      const url = this._createTextDataUrl(s.text.content, s.text.color, s.text.fontSize);
      c.applyPrint(part, url, s.text.rotation ?? 0);
    } else {
      c.clearPrint(part);
    }
  }

  initPrinting() {
    this._state = {
      cap:  { logo: { filename: null, dataUrl: null, rotation: 0 }, text: { content: '', fontSize: 'medium', color: null, colorName: null, rotation: 0 } },
      body: { logo: { filename: null, dataUrl: null, rotation: 0 }, text: { content: '', fontSize: 'medium', color: null, colorName: null, rotation: 0 } },
    };

    const log = () => console.log('[BycPrinting]', JSON.stringify(this._state, null, 2));
    const getPart = (el) => el.closest('[tab-content]')?.dataset.id ?? null;
    const capsule  = () => document.querySelector('byc-capsule');

    // Clear the logo UI for a given tab-content id (part).
    // Multiple [tab-content][data-id="cap/body"] exist (color pickers also use them),
    // so find specifically the one that contains the upload area.
    const clearLogoUI = (part) => {
      const tabContent = [...this.querySelectorAll(`[tab-content][data-id="${part}"]`)]
        .find(el => el.querySelector('.byc-upload-preview'));
      if (!tabContent) return;
      const input   = tabContent.querySelector('.byc-upload-input');
      const preview = tabContent.querySelector('.byc-upload-preview');
      const ph      = tabContent.querySelector('.byc-upload-placeholder');
      if (input)   input.value = '';
      if (preview) { preview.removeAttribute('src'); preview.setAttribute('hidden', ''); }
      if (ph)      ph.removeAttribute('hidden');
      this._state[part].logo = { filename: null, dataUrl: null, rotation: 0 };
    };

    // Clear the text UI for a given tab-content id (part).
    const clearTextUI = (part) => {
      const tabContent = [...this.querySelectorAll(`[tab-content][data-id="${part}"]`)]
        .find(el => el.querySelector('.byc-text-input'));
      if (!tabContent) return;
      const textInput = tabContent.querySelector('.byc-text-input');
      if (textInput) textInput.value = '';
      this._state[part].text.content = '';
      this._state[part].text.rotation = 0;
    };

    // helper: re-render whatever is active for a part (logo takes precedence over text)
    const renderPart = (part) => this._renderPart(part);

    // ── File uploads ───────────────────────────────────────────────────
    this.querySelectorAll('.byc-upload-input').forEach((input) => {
      input.addEventListener('change', () => {
        const part = getPart(input);
        if (!part) return;
        const file = input.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
          const dataUrl = ev.target.result;
          this._state[part].logo = { filename: file.name, dataUrl, rotation: 0 };

          // Uploading an image clears any text for this part.
          clearTextUI(part);

          const area = input.closest('.byc-upload-area');
          const preview = area?.querySelector('.byc-upload-preview');
          const placeholder = area?.querySelector('.byc-upload-placeholder');
          if (preview) { preview.src = dataUrl; preview.removeAttribute('hidden'); }
          if (placeholder) placeholder.setAttribute('hidden', '');

          renderPart(part);
          log();
          this._notifySummary();
        };
        reader.readAsDataURL(file);
      });
    });

    // ── Rotate buttons ─────────────────────────────────────────────────
    this.querySelectorAll('.byc-btn--rotate').forEach((btn) => {
      btn.addEventListener('click', () => {
        const part = getPart(btn);
        if (!part) return;
        const inner = btn.closest('.tabs-switch--inner');
        const isLogo = !!inner?.querySelector('.byc-upload-area');
        if (isLogo) {
          this._state[part].logo.rotation = (this._state[part].logo.rotation + 45) % 360;
        } else {
          this._state[part].text.rotation = (this._state[part].text.rotation + 45) % 360;
        }
        renderPart(part);
        log();
        this._notifySummary();
      });
    });

    // ── Delete buttons ─────────────────────────────────────────────────
    this.querySelectorAll('.byc-btn--delete').forEach((btn) => {
      btn.addEventListener('click', () => {
        const part = getPart(btn);
        if (!part) return;
        const inner = btn.closest('.tabs-switch--inner');
        if (!inner) return;

        const uploadArea = inner.querySelector('.byc-upload-area');
        if (uploadArea) {
          this._state[part].logo = { filename: null, dataUrl: null, rotation: 0 };
          const input   = uploadArea.querySelector('.byc-upload-input');
          const preview = uploadArea.querySelector('.byc-upload-preview');
          const ph      = uploadArea.querySelector('.byc-upload-placeholder');
          if (input) input.value = '';
          if (preview) { preview.src = ''; preview.setAttribute('hidden', ''); }
          if (ph) ph.removeAttribute('hidden');
        } else {
          this._state[part].text = { content: '', fontSize: 'medium', color: this._state[part].text.color, colorName: this._state[part].text.colorName, rotation: 0 };
          const textInput = inner.querySelector('.byc-text-input');
          if (textInput) textInput.value = '';
          inner.querySelectorAll('input[type="radio"][name$="_font_size"]').forEach(r => { r.checked = r.value === 'medium'; });
        }

        renderPart(part);
        log();
        this._notifySummary();
      });
    });

    // ── Text inputs ────────────────────────────────────────────────────
    this.querySelectorAll('.byc-text-input').forEach((input) => {
      input.addEventListener('input', () => {
        const part = getPart(input);
        if (!part) return;
        this._state[part].text.content = input.value;

        // Typing text clears any uploaded image for this part.
        if (input.value) clearLogoUI(part);

        renderPart(part);
        log();
        this._notifySummary();
      });
    });

    // ── Font size radios ───────────────────────────────────────────────
    this.querySelectorAll('input[name="cap_font_size"], input[name="body_font_size"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const part = radio.name.startsWith('cap') ? 'cap' : 'body';
        this._state[part].text.fontSize = radio.value;
        renderPart(part);
        log();
        this._notifySummary();
      });
    });

    // ── Font colour swatches ───────────────────────────────────────────
    this.querySelectorAll('.byc-swatch-input').forEach((radio) => {
      radio.addEventListener('change', () => {
        const part = getPart(radio);
        if (!part) return;
        this._state[part].text.color     = radio.value;
        this._state[part].text.colorName = radio.dataset.name ?? null;
        renderPart(part);
        log();
        this._notifySummary();
      });
    });
  }

  selectType(item) {
    this.querySelectorAll(this.selectors.typeItem).forEach((i) =>
      i.classList.toggle('is-active', i === item)
    );

    const radio = item.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    const type = item.dataset.value;
    this._capsuleType = type;
    console.log('[BycControls] type selected:', type);

    // Reset color state so summary reflects fresh selections for the new type.
    this._state = this._state ?? {};
    this._state.color = {};
    // Reset print state too.
    this._state.cap  = { logo: { filename: null, dataUrl: null, rotation: 0 }, text: { content: '', fontSize: 'medium', color: null, colorName: null, rotation: 0 } };
    this._state.body = { logo: { filename: null, dataUrl: null, rotation: 0 }, text: { content: '', fontSize: 'medium', color: null, colorName: null, rotation: 0 } };
    // Uncheck all color radios so no stale swatch appears selected.
    this.querySelectorAll('input[type="radio"][name^="capsule-color-"]').forEach(r => r.checked = false);
    // Uncheck all size radios and remove active state so summary shows no size for new type.
    this.querySelectorAll('input[type="radio"][name^="capsule-size"]').forEach(r => r.checked = false);
    this.querySelectorAll(this.selectors.sizeItem).forEach(i => i.classList.remove('is-active'));
    // Clear text fields.
    this.querySelectorAll('.byc-text-input').forEach(i => i.value = '');
    // Clear file pickers and hide upload previews.
    this.querySelectorAll('.byc-upload-input').forEach(i => i.value = '');
    this.querySelectorAll('.byc-upload-preview').forEach(img => { img.removeAttribute('src'); img.setAttribute('hidden', ''); });
    this.querySelectorAll('.byc-upload-placeholder').forEach(p => p.removeAttribute('hidden'));

    // LIQ_CAPS has a visible banda (center band/thread). All other types hide it.
    const capsule = document.querySelector('byc-capsule');
    if (capsule) capsule.applyTypeReset(type === 'LIQ_CAPS', type);

    // Show only [data-type] inners matching the selected type.
    // Skip the type list's own inner (it contains typeItem elements).
    this.querySelectorAll('[data-type]').forEach((inner) => {
      if (inner.querySelector(this.selectors.typeItem)) return;
      inner.hidden = inner.dataset.type !== type;
    });

    // Detect whether this type has any colour options (swatches or wheel pickers).
    // An inner that matches the type inside the colors accordion is used as the check.
    const colorInner = [...this.querySelectorAll(`.byc-accordion--colors [data-type="${type}"]`)]
      .find(el => !el.querySelector(this.selectors.typeItem));
    this._typeHasColors = colorInner
      ? !!(colorInner.querySelector('input[type="radio"]') || colorInner.querySelector('byc-color-picker'))
      : false;

    this._notifySummary();
  }

  selectSize(item) {
    // Deselect all sizes in the same list, mark the clicked one active.
    const list = item.closest('.accordion-opt__list--sizes');
    if (!list) return;
    list.querySelectorAll(this.selectors.sizeItem).forEach((i) =>
      i.classList.toggle('is-active', i === item)
    );

    const radio = item.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    console.log('[BycControls] size selected:', item.dataset.value);
    this._notifySummary();
  }
}

customElements.define('byc-controls', BycControls);


/* ── BycColorPicker TABS ────────────────────────────────────────────────── */
class TabsSwitch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      tabBtn: '[tab-btn]',
      tabContent: '[tab-content]',
      indicator: '.tabs-buttons__indicator',
    };
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.tabBtns = [...this.querySelectorAll(this.selectors.tabBtn)];
    this.tabContents = [...this.querySelectorAll(this.selectors.tabContent)];
    this.indicator = this.querySelector(this.selectors.indicator);

    if (!this.tabBtns.length) return;

    this.bindEvents();

    // Activate the tab pre-marked active in Liquid, or fall back to first.
    const activeBtn =
      this.tabBtns.find((b) => b.classList.contains('is-active')) || this.tabBtns[0];
    this.switchTab(activeBtn);

    // moveIndicator() needs real layout from getBoundingClientRect().
    // The component starts inside a collapsed accordion (display:none chain),
    // so layout is zero until the accordion opens.
    // IntersectionObserver fires exactly when the element first has visible area
    // (i.e. when the parent accordion is opened), giving us correct measurements.
    if (this.indicator) {
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          this.moveIndicator(this.tabBtns.find((b) => b.classList.contains('is-active')));
        },
        { threshold: 0.01 }
      );
      io.observe(this);
    }
  }

  bindEvents() {
    this.tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.switchTab(btn));
    });
  }

  switchTab(activeBtn) {
    const id = activeBtn.dataset.id;

    // buttons
    this.tabBtns.forEach((btn) => {
      btn.classList.toggle('is-active', btn === activeBtn);
    });

    // indicator
    this.moveIndicator(activeBtn);

    // content
    this.tabContents.forEach((content) => {
      content.classList.toggle(
        'is-active',
        content.dataset.id === id
      );
    });
  }

  moveIndicator(btn) {
    if (!this.indicator || !btn) return;

    const parent = this.indicator.parentElement;

    const parentRect = parent.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    const left = btnRect.left - parentRect.left;

    this.indicator.style.width = `${btnRect.width}px`;
    this.indicator.style.transform = `translateX(${left}px)`;
  }
}

customElements.define('tabs-switch', TabsSwitch);


/* ── BycSummary ─────────────────────────────────────────────────────── */
class BycSummary extends HTMLElement {
  connectedCallback() {
    // Close button
    this.querySelector('.byc-summary-modal__close')?.addEventListener('click', () => this.close());

    // Click outside modal to close
    this.querySelector('.byc-summary-overlay')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.close();
    });

    // Keyboard Escape to close
    this._keyHandler = (e) => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this._keyHandler);

    // Live-update whenever any field changes.
    this._stateHandler = () => { if (this.classList.contains('is-open')) this._populate(); };
    document.addEventListener('byc:state-change', this._stateHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._keyHandler);
    document.removeEventListener('byc:state-change', this._stateHandler);
  }

  open() {
    this._populate();
    this.classList.add('is-open');
    document.body.classList.add('byc-summary-open');
    this.querySelector('.byc-summary-modal__close')?.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('byc-summary-open');
  }

  // ── Helpers ───────────────────────────────────────────────────────
  _set(selector, html) {
    const el = this.querySelector(selector);
    if (!el) return;
    el.innerHTML = html;
    el.classList.toggle('bys-empty', !html || html === '—');
  }

  _colorCell(hex, name) {
    if (!hex) return '<span class="bys-empty">—</span>';
    return `<span class="bys-color-cell">
      <span class="bys-color-swatch" style="background:${hex}"></span>
      <span>${name ? name : hex}</span>
    </span>`;
  }

  _printCell(state) {
    if (state?.dataUrl) {
      return `<img class="bys-print-thumb" src="${state.dataUrl}" alt="Logo">`;
    }
    if (state?.content) {
      return `<span>${state.content}</span>`;
    }
    return '<span class="bys-empty">—</span>';
  }

  _populate() {
    const controls = document.querySelector('byc-controls');
    if (!controls) return;

    const st     = controls._state  ?? {};
    const colors = st.color         ?? {};

    // ── Capsule type ──────────────────────────────────────────────
    const typeLabel = controls.querySelector('.byc-opt__type-item.is-active label')?.textContent?.trim() ?? '—';
    this._set('.bys-type', typeLabel);

    // ── Capsule size ──────────────────────────────────────────────
    const sizeRadio = controls.querySelector('input[name="capsule-size"]:checked');
    const sizeLabel = sizeRadio
      ? (sizeRadio.closest('.byc-opt__size-item')?.querySelector('label')?.textContent?.trim() ?? sizeRadio.value)
      : '—';
    this._set('.bys-size', sizeLabel);

    // ── Colors ────────────────────────────────────────────────────
    // Flavour stores into both cap+body keys, so always read cap/body directly.
    const capColor  = colors.cap      ?? null;
    const bodyColor = colors.body     ?? null;
    const branding  = colors.branding ?? null;

    this._set('.bys-cap-color',  this._colorCell(capColor?.hex,  capColor?.name));
    this._set('.bys-body-color', this._colorCell(bodyColor?.hex, bodyColor?.name));

    // Branding band row — only for LIQ_CAPS
    const brandingRow = this.querySelector('.bys-branding-row');
    const isLiq = controls._capsuleType === 'LIQ_CAPS';
    if (brandingRow) brandingRow.hidden = !isLiq;
    if (isLiq) {
      this._set('.bys-branding', this._colorCell(branding?.hex, null));
    }

    // ── Prints ────────────────────────────────────────────────────
    this._set('.bys-cap-print',  this._printCell(
      st.cap?.logo?.dataUrl ? st.cap.logo : (st.cap?.text?.content ? st.cap.text : null)
    ));
    this._set('.bys-body-print', this._printCell(
      st.body?.logo?.dataUrl ? st.body.logo : (st.body?.text?.content ? st.body.text : null)
    ));

    // ── 3D canvas screenshot ──────────────────────────────────────
    const capsule = document.querySelector('byc-capsule');
    const img     = this.querySelector('.byc-summary-img');
    if (img && capsule) {
      try {
        capsule.render(); // ensure latest frame
        const dataUrl = capsule.renderer.domElement.toDataURL('image/png');
        img.src = dataUrl;
        img.hidden = false;
      } catch (_) {
        img.hidden = true;
      }
    }
  }
}

customElements.define('byc-summary', BycSummary);


/* ── BycQuoteModal ──────────────────────────────────────────────────── */
class BycQuoteModal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      overlay: '.byc-quote-modal__overlay',
      dialog:  '.byc-quote-modal__dialog',
      close:   '.byc-quote-modal__close',
    };
    // All focusable elements that can receive keyboard focus inside the dialog.
    this._focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Close button
    this.querySelector(this.selectors.close)?.addEventListener('click', () => this.close());

    // Click on backdrop (overlay) — but not on the dialog itself — closes modal.
    this.querySelector(this.selectors.overlay)?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.close();
    });

    // Keyboard: Escape to close, Tab to trap focus.
    this._keyHandler = (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') this._trapFocus(e);
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._keyHandler);
  }

  open() {
    // Store the element that had focus before opening so we can restore it on close.
    this._returnFocus = document.activeElement;

    // Bump generation so any in-flight close animationend handler becomes stale
    // and will not remove is-open from the freshly re-opened modal.
    this._gen = (this._gen ?? 0) + 1;

    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('byc-quote-modal-open');

    // Always re-populate the capsule details field with the latest state.
    this._populateSummaryField();

    // Populate Globo form fields (textareas + file upload) once they render.
    this._populateGloboForm();

    // Move focus to the close button for immediate keyboard accessibility.
    this.querySelector(this.selectors.close)?.focus();
  }

  // ── Globo form population ──────────────────────────────────────────
  /**
   * Poll until the Globo form builder has injected its elements, then:
   * 1. Fill every .byd-specs-element textarea with the capsule summary.
   * 2. Capture the 3-D model + any logo images and attach them to
   *    .byc-file-upload-element input[type="file"].
   */
  _populateGloboForm(attempt = 0) {
    const MAX_ATTEMPTS  = 40;   // ~12 s total
    const POLL_INTERVAL = 300;  // ms

    const textareas = document.querySelectorAll('.byd-specs-element textarea');
    const fileInput = document.querySelector('.byc-file-upload-element input[type="file"]');

    if (!textareas.length && !fileInput) {
      if (attempt < MAX_ATTEMPTS) {
        setTimeout(() => this._populateGloboForm(attempt + 1), POLL_INTERVAL);
      }
      return;
    }

    // ── 1. Fill textareas with the capsule summary ─────────────────
    const summary = this._buildSummaryText();
    textareas.forEach((ta) => {
      ta.value = summary;
      ta.setAttribute('readonly', '');
      ta.dispatchEvent(new Event('input',  { bubbles: true }));
      ta.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // ── 2. Inject images into the file input ───────────────────────
    if (fileInput) {
      this._injectImages(fileInput);
    }
  }

  /** Build a plain-text summary string (reused from _populateSummaryField logic). */
  _buildSummaryText() {
    const controls = document.querySelector('byc-controls');
    if (!controls) return '';

    const st     = controls._state ?? {};
    const colors = st.color        ?? {};

    const type = controls.querySelector('.byc-opt__type-item.is-active label')?.textContent?.trim() ?? '';

    const sizeRadio = controls.querySelector('input[name="capsule-size"]:checked');
    const size = sizeRadio
      ? (sizeRadio.closest('.byc-opt__size-item')?.querySelector('label')?.textContent?.trim() ?? sizeRadio.value)
      : '';

    const colorLabel = (c) => (c?.name ? c.name : (c?.hex ?? ''));
    const capColor   = colors.cap;
    const bodyColor  = colors.body;
    const bandColor  = colors.branding;

    const lines = [];
    if (type)  lines.push(`Type: ${type}`);
    if (size)  lines.push(`Size: ${size}`);
    if (capColor?.hex)  lines.push(`Cap Color: ${colorLabel(capColor)}`);
    if (bodyColor?.hex) lines.push(`Body Color: ${colorLabel(bodyColor)}`);
    if (bandColor?.hex && controls._capsuleType === 'LIQ_CAPS') lines.push(`Band Color: ${colorLabel(bandColor)}`);

    const addPrint = (label, part) => {
      const s = st[part];
      if (!s) return;
      if (s.logo?.filename) {
        lines.push(`${label}:`);
        lines.push(`  Logo: ${s.logo.filename}`);
        if (s.logo.rotation) lines.push(`  Rotation: ${s.logo.rotation}\u00B0`);
      } else if (s.text?.content) {
        lines.push(`${label}:`);
        lines.push(`  Text: "${s.text.content}"`);
        if (s.text.fontSize)  lines.push(`  Size: ${s.text.fontSize}`);
        if (s.text.colorName || s.text.color) lines.push(`  Color: ${s.text.colorName ?? s.text.color}`);
        if (s.text.rotation)  lines.push(`  Rotation: ${s.text.rotation}\u00B0`);
      }
    };

    addPrint('Cap Print', 'cap');
    addPrint('Body Print', 'body');

    return lines.join('\n');
  }

  /**
   * Build a FileList from the 3-D model screenshot and any uploaded logos,
   * then assign it to the given file input.
   */
  async _injectImages(fileInput) {
    const capsule  = document.querySelector('byc-capsule');
    const controls = document.querySelector('byc-controls');

    // ── Clear any previously injected files first ──────────────────
    try {
      const empty = new DataTransfer();
      fileInput.files = empty.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (_) {}

    // ── Wait for any in-flight texture loads before capturing ──────
    if (capsule?.waitForTextures) {
      await capsule.waitForTextures();
    }

    // ── Reset to default position so the capture is always consistent ──
    if (capsule?.resetPosition) {
      capsule.resetPosition();
    }

    // ── Wait 1 s for the renderer to fully settle after the reset ──
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const dt = new DataTransfer();

    // ── 3-D model screenshot ───────────────────────────────────────
    if (capsule) {
      try {
        const dataUrl = capsule.captureImage(1200, 900);
        const blob    = this._dataUrlToBlob(dataUrl);
        dt.items.add(new File([blob], 'capsule-model.png', { type: 'image/png' }));
      } catch (err) {
        console.warn('[BycQuoteModal] captureImage failed:', err);
      }
    }

    // ── Uploaded logo(s) ───────────────────────────────────────────
    if (controls?._state) {
      ['cap', 'body'].forEach((part) => {
        const logo = controls._state[part]?.logo;
        if (!logo?.dataUrl || !logo?.filename) return;
        try {
          const blob = this._dataUrlToBlob(logo.dataUrl);
          dt.items.add(new File([blob], logo.filename, { type: blob.type }));
        } catch (err) {
          console.warn(`[BycQuoteModal] logo inject failed (${part}):`, err);
        }
      });
    }

    if (dt.files.length) {
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** Convert a data-URL string to a Blob. */
  _dataUrlToBlob(dataUrl) {
    const [header, b64] = dataUrl.split(',');
    const mime   = header.match(/:(.*?);/)[1];
    const binary = atob(b64);
    const arr    = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // ── Capsule summary → form field ──────────────────────────────────
  _populateSummaryField() {
    const input = this.querySelector('input[name="00N3s00000PhX0Q"]');
    if (!input) return;

    const controls = document.querySelector('byc-controls');
    if (!controls) return;

    const st     = controls._state ?? {};
    const colors = st.color        ?? {};

    // Capsule type
    const type = controls.querySelector('.byc-opt__type-item.is-active label')?.textContent?.trim() ?? '';

    // Capsule size
    const sizeRadio = controls.querySelector('input[name="capsule-size"]:checked');
    const size = sizeRadio
      ? (sizeRadio.closest('.byc-opt__size-item')?.querySelector('label')?.textContent?.trim() ?? sizeRadio.value)
      : '';

    // Colors — prefer name, fall back to hex
    const capColor  = colors.cap;
    const bodyColor = colors.body;
    const bandColor = colors.branding;

    const colorLabel = (c) => (c?.name ? c.name : (c?.hex ?? ''));

    // Print description for a part
    const printLabel = (part) => {
      const s = st[part];
      if (!s) return '';
      if (s.logo?.filename) {
        const rot = s.logo.rotation ? `, rotated ${s.logo.rotation}\u00B0` : '';
        return `Logo: ${s.logo.filename}${rot}`;
      }
      if (s.text?.content) {
        const fontSize = s.text.fontSize ? `, ${s.text.fontSize}` : '';
        const color = s.text.colorName ? `, ${s.text.colorName}` : (s.text.color ? `, ${s.text.color}` : '');
        const rot = s.text.rotation ? `, rotated ${s.text.rotation}\u00B0` : '';
        return `Text: "${s.text.content}"${fontSize}${color}${rot}`;
      }
      return '';
    };

    // Build parts array, skip empty segments
    const parts = [];
    if (type)                                   parts.push(`Type: ${type}`);
    if (size)                                   parts.push(`Size: ${size}`);
    if (capColor?.hex)                          parts.push(`Cap: ${colorLabel(capColor)}`);
    if (bodyColor?.hex)                         parts.push(`Body: ${colorLabel(bodyColor)}`);
    if (bandColor?.hex && controls._capsuleType === 'LIQ_CAPS') parts.push(`Band: ${colorLabel(bandColor)}`);
    const capPrint  = printLabel('cap');
    const bodyPrint = printLabel('body');
    if (capPrint)                               parts.push(`Cap Print: ${capPrint}`);
    if (bodyPrint)                              parts.push(`Body Print: ${bodyPrint}`);

    // Join with separator and enforce maxlength of 255
    let value = parts.join(' | ');
    if (value.length > 255) value = value.slice(0, 252) + '...';

    input.value = value;
  }

  close() {
    // Play exit animation then remove is-open so the overlay returns to display:none.
    this.classList.add('is-closing');

    // Snapshot the generation at the time close() was called.
    // If open() is called before the animation ends, the generation will have
    // changed and this handler becomes a no-op — preventing the stale cleanup
    // from hiding the freshly re-opened modal.
    const closedGen = this._gen ?? 0;

    const dialog = this.querySelector(this.selectors.dialog);
    if (dialog) {
      dialog.addEventListener(
        'animationend',
        () => {
          if ((this._gen ?? 0) !== closedGen) return; // open() fired before we finished
          this.classList.remove('is-open', 'is-closing');
          document.body.classList.remove('byc-quote-modal-open');
          // Return focus to the trigger element.
          this._returnFocus?.focus();
        },
        { once: true }
      );
    } else {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('byc-quote-modal-open');
      this._returnFocus?.focus();
    }
  }

  // ── Focus trap ────────────────────────────────────────────────────
  _trapFocus(e) {
    const dialog = this.querySelector(this.selectors.dialog);
    if (!dialog) return;

    const focusable = [...dialog.querySelectorAll(this._focusableSelectors)].filter(
      (el) => !el.closest('[hidden]') && el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      // Shift+Tab: if focus is on first, wrap to last.
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab: if focus is on last, wrap to first.
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

customElements.define('byc-quote-modal', BycQuoteModal);
